import {
  getCurrency,
  getFormattedValueWithCurrency,
  getValueWithCurrency,
  type GetCurrency,
  type GetFormattedValueWithCurrency,
  type GetValueWithCurrency
} from './currency';
import { type GetGoogleVerificationSite, getGoogleVerificationSite } from './googleVerificationSite';
import { PAGES, type Page } from './page';
import { type GetFacebookMeta, getFacebookMeta } from './facebook';
import { DEFAULT_LANGUAGE_ISO, getLanguageIdByLanguageIso, getLanguageIso, type LanguageIso } from './language';

export const DACHLU_COUNTRY_IDS = [1, 17, 3, 4];
export const BRAND_ID = 1;
export const COUNTRY_ISO = {
  AT: 'AT',
  BE: 'BE',
  BG: 'BG',
  CH: 'CH',
  CZ: 'CZ',
  DE: 'DE',
  DK: 'DK',
  EN: 'EN',
  ES: 'ES',
  FI: 'FI',
  FR: 'FR',
  GB: 'GB',
  HU: 'HU',
  IE: 'IE',
  IT: 'IT',
  LU: 'LU',
  NL: 'NL',
  PL: 'PL',
  PT: 'PT',
  SE: 'SE'
};

/** Countries outside the European Union */
export const NON_EUROPEAN_COUNTRY_ISO: readonly CountryIso[] = ['IE', 'CH', 'DK', 'SE', 'GB'];

export type CountryIso = keyof typeof COUNTRY_ISO;

export type LanguageSwitcher = {
  link: string;
  title: string;
};

export type CountryLanguageOptions = { [countryCode in CountryIso]?: LanguageSwitcher[] };

export interface Country {
  countryId: number;
  code: CountryIso;
  dialingCode: number;
  language: LanguageIso;
  languageId: number;
  brandId: number;
  pages: Page[];
  whatsappSupport: boolean;
  getCurrency: GetCurrency;
  getValueWithCurrency: GetValueWithCurrency;
  getFormattedValueWithCurrency: GetFormattedValueWithCurrency;
  getGoogleVerificationSite?: GetGoogleVerificationSite;
  getFacebookMeta?: GetFacebookMeta;
}

export function createCountry(
  countryId: number,
  code: CountryIso,
  dialingCode: number,
  pages: Page[],
  whatsappSupport = true
): Country {
  return {
    countryId,
    code,
    dialingCode,
    pages,
    language: DEFAULT_LANGUAGE_ISO,
    languageId: 0,
    brandId: BRAND_ID,
    whatsappSupport,
    getCurrency,
    getGoogleVerificationSite,
    getFacebookMeta,
    getValueWithCurrency,
    getFormattedValueWithCurrency
  };
}

export const COUNTRIES: Country[] = [
  createCountry(6, 'BE', 32, [PAGES['Belgie'], PAGES['Belgique']], false),
  createCountry(21, 'BG', 359, [PAGES['Bulgaria']]),
  createCountry(19, 'CZ', 420, [PAGES['Czech']]),
  createCountry(10, 'DK', 45, [PAGES['Denmark']]),
  createCountry(1, 'DE', 49, [PAGES['Germany']]),
  createCountry(3, 'AT', 43, [PAGES['Austria']]),
  createCountry(4, 'CH', 41, [PAGES['Schweiz'], PAGES['Suisse'], PAGES['Svizzera']]),
  createCountry(17, 'LU', 352, [PAGES['Luxembourg']]),
  createCountry(8, 'ES', 34, [PAGES['Spain']]),
  createCountry(5, 'FR', 33, [PAGES['France']]),
  createCountry(20, 'HU', 36, [PAGES['Hungary']]),
  createCountry(15, 'IE', 353, [PAGES['Ireland']]),
  createCountry(7, 'IT', 39, [PAGES['Italy']]),
  createCountry(2, 'NL', 31, [PAGES['Netherlands']], false),
  createCountry(18, 'PL', 48, [PAGES['Poland']]),
  createCountry(9, 'PT', 351, [PAGES['Portugal']]),
  createCountry(14, 'FI', 358, [PAGES['Finland']]),
  createCountry(11, 'SE', 46, [PAGES['Sweden']]),
  createCountry(16, 'GB', 44, [PAGES['UK']]),
  createCountry(0, 'EN', 1, [PAGES['International']])
];

export function getCountryIso(locale: string) {
  if (!locale.includes('-') || locale.length === 2) {
    return locale.toLocaleUpperCase() as CountryIso;
  }

  return locale.split('-')[1] as CountryIso;
}

/**
 * Gets the matching Country for a given locale (e.g. 'de-DE' → 'DE')
 * @param locale
 * @returns The matching Country, or undefined if not found
 */
export function getCountryByLocale(locale: string): Country {
  const countryCode = getCountryIso(locale);
  const country = COUNTRIES.find(c => c.code === countryCode);
  const language = getLanguageIso(locale);
  const languageId = getLanguageIdByLanguageIso(language);

  if (!country) {
    throw new Error(`Unknown locale: ${locale}`);
  }

  country.language = language;
  country.languageId = languageId;

  return country;
}

export function getCountryLanguageOptions(): CountryLanguageOptions {
  // Only include countries with multiple language options (pages > 1) for the switcher
  const countries = COUNTRIES.filter(country => country.pages.length > 1);

  return countries.reduce(
    (result, country) => ({
      ...result,
      [country.code]: country.pages.map(page => ({
        link: page.homepage,
        title: `${page.name} - ${getLanguageIso(page.locale).toUpperCase()}`
      }))
    }),
    {} as CountryLanguageOptions
  );
}

export function isDACHLU(countryId: number): boolean {
  return DACHLU_COUNTRY_IDS.includes(countryId);
}

export function isCountryDE(countryId: number): boolean {
  return countryId === 1;
}

export function isNonEuropeanCountry(countryIso: string): boolean {
  return NON_EUROPEAN_COUNTRY_ISO.some(nonEuropeanCountryIso => nonEuropeanCountryIso === countryIso.toUpperCase());
}
